import type React from 'react';
import { type ReactNode, useCallback, useEffect, useState } from 'react';

import * as styles from './styles';
import { cn } from '@cobliteam/ui/utils';
import moment from 'moment';

import { type Incident, IncidentType } from '@cobliteam/api-lib';

import { useGeocode } from '@cobliteam/hooks';

import {
  CaptionDefault,
  ContainerDefault,
  ContentLoader,
  DescriptionListDefault,
  DescriptionListMedium,
  DescriptionTitle,
  DevicePluggedIcon,
  Divider,
  InfoIcon,
  Tag,
  UnpluggedIcon,
  WarningIcon,
} from '@cobliteam/base-components';

import type { IncidentCluster } from '../Tracking/components/Map';

export interface IncidentPopupProps {
  incidentOrCluster: Incident | IncidentCluster;
  driverName?: string;
  showAdditionalInfo?: boolean;
  duration?: number;
  arrow?: boolean;
  children?: ReactNode;
}

const AddressPlaceholder = () => (
  <ContentLoader width={250} height={14} className={styles.addressPlaceholder}>
    <rect x='0' y='0' width='100%' height='100%' />
  </ContentLoader>
);

export const IncidentPopup: React.FC<IncidentPopupProps> = ({ incidentOrCluster: incident, driverName, showAdditionalInfo, duration, arrow = true, children }) => {
  const { getAddress } = useGeocode();
  const [address, setAddress] = useState('');

  const isCluster = 'incidents' in incident;
  const { type, latitude, longitude, time } = incident;

  const formatHour = (valueInMillis: number): string => moment(valueInMillis, 'x').format('HH:mm[h]');

  const durationInHours = (valueInMillis: number): string => `${valueInMillis / (1000 * 60 * 60)}h`;

  const deviceStatusText = type === IncidentType.DEVICE_PLUGGED ? 'Conectado em' : 'Desconectado em';

  const getTitle = () => {
    return type === IncidentType.DEVICE_PLUGGED ? 'Dispositivo conectado' : 'Dispositivo desconectado';
  };

  const renderInfoRow = (label: string, value: string | JSX.Element) => (
    <DescriptionListDefault>
      <DescriptionListMedium className={styles.infoLabel}>{`${label}:`}</DescriptionListMedium>
      {value}
    </DescriptionListDefault>
  );

  const renderAdditionalInfoBanner = () => {
    if (duration) {
      return (
        <div className={styles.pluggedBanner}>
          <InfoIcon />
          <CaptionDefault>Esse dispositivo já está conectado</CaptionDefault>
        </div>
      );
    }

    return (
      <div className={styles.unpluggedBanner}>
        <WarningIcon />
        <CaptionDefault>Entre em contato com o agente de campo para entender o que aconteceu com o dispositivo</CaptionDefault>
      </div>
    );
  };

  const handleAddress = useCallback(async () => {
    if (!latitude || !longitude) return;

    const latLng = { lat: latitude, lng: longitude };
    const responseAddress = await getAddress(latLng);
    setAddress(responseAddress);
  }, [latitude, longitude, getAddress]);

  useEffect(() => {
    handleAddress();
  }, [handleAddress]);

  return (
    <ContainerDefault className={cn(styles.popup, arrow ? styles.popupArrow : '')}>
      <div className={styles.titleWrapper}>
        <div className={styles.header}>
          {type === IncidentType.DEVICE_PLUGGED ? <DevicePluggedIcon className={styles.pluggedIcon} /> : <UnpluggedIcon className={styles.unpluggedIcon} />}
          <DescriptionTitle>{getTitle()}</DescriptionTitle>
          {isCluster && (
            <div className={styles.tagContainer}>
              <Tag className={styles.tag} label={`${incident.incidents.length} eventos neste endereço`} />
            </div>
          )}
        </div>
      </div>

      <div className={styles.body}>
        {isCluster && (
          <div className={styles.warningMessage}>
            <WarningIcon className={styles.warningIcon} />
            <CaptionDefault>Veja se o dispositivo está bem conectado à sua entrada</CaptionDefault>
          </div>
        )}
        {renderInfoRow(deviceStatusText, formatHour(time))}
        {renderInfoRow('Endereço', address || <AddressPlaceholder />)}
        {driverName && renderInfoRow('Motorista', driverName)}
        {showAdditionalInfo && duration && renderInfoRow('Duração', durationInHours(duration))}
        {showAdditionalInfo && renderAdditionalInfoBanner()}
        <Divider className={styles.divider} />
        {children}
      </div>
    </ContainerDefault>
  );
};
